import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import Select from "react-select";
import CustomCalendar from "./Custom_Calendar";
import { ReactComponent as CalenderIcon } from "../../assets/icons/svg/calendarr-icon.svg";
import { ReactComponent as InformationCircle } from "../../assets/icons/svg/information-circle-icon.svg";

// Define the interface for the duration option
interface DurationOption {
  value: string;
  label: string;
}

// Define the interface for the component props
interface NowScheduleData {
  endDate: Date;
  duration: DurationOption | null;
}

interface NewSelectorCardProps {
  initialData?: NowScheduleData;
  onDataChange: (data: NowScheduleData) => void;
  durationOptions?: DurationOption[]; // Prop for custom duration options
}

// Styled dropdown
const customSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    borderRadius: 8,
    borderColor: "#d1d5db",
    padding: 2,
    boxShadow: "none",
    "&:hover": { borderColor: "#a1a1aa" },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    padding: 10,
    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.15)",
    width: "95%",
    margin: 6,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    borderRadius: 8,
    backgroundColor: state.isSelected ? "#eff6ff" : "#ffffff", // Highlight only selected option
    color: "#1f2937",
    padding: 12,
    marginBottom: 8,
    cursor: "pointer",
    transition: "background-color 0.2s",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#1f2937",
  }),
};

const NewSelectorCard: React.FC<NewSelectorCardProps> = ({
  initialData,
  onDataChange,
  durationOptions = [
    { value: "10 sec", label: "10 sec" },
    { value: "20 sec", label: "20 sec" },
    { value: "30 sec", label: "30 sec" },
  ],
}) => {
  const [endDate, setEndDate] = useState<Date>(
    initialData?.endDate || new Date()
  );
  const [duration, setDuration] = useState<DurationOption | null>(
    initialData?.duration || null
  );
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);

  // Initialize state from initialData when component mounts
  useEffect(() => {
    if (initialData && !hasInitialized.current) {
      setEndDate(initialData.endDate || new Date());
      setDuration(initialData.duration || null);
      hasInitialized.current = true;
    }
  }, [initialData]);

  // Update parent when data changes (including initial setup)
  useEffect(() => {
    const data: NowScheduleData = {
      endDate,
      duration,
    };
    onDataChange(data);
  }, [endDate, duration, onDataChange]);

  // Close calendar on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowEndCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
    setShowEndCalendar(false);
  };

  const handleDurationChange = (selected: DurationOption | null) => {
    setDuration(selected);
    setShowEndCalendar(false); // Close calendar when duration is changed
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Info banner */}
      <div className="text-xs 2xl:text-sm text-[#0BA6C7] mb-2 w-full">
        <div className="flex items-center bg-blue-50 px-4 py-2 rounded-lg w-full">
          <InformationCircle className="w-4 h-4 mr-2 text-blue-600" />
          <span>End date is mandatory.</span>
        </div>
      </div>

      {/* End Date */}
      <div className="relative" ref={calendarRef}>
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          End date<span className="text-red-500">*</span>:
        </label>
        <div className="relative">
          <input
            readOnly
            value={format(endDate, "dd/MM/yyyy")}
            onClick={() => setShowEndCalendar(!showEndCalendar)}
            className="w-full border border-gray-300 rounded px-3 py-2 cursor-pointer"
          />
          <span className="absolute right-3 top-2.5 pointer-events-none">
            <CalenderIcon width={24} height={24} />
          </span>
        </div>
        {showEndCalendar && (
          <div className="absolute z-10 mt-2">
            <CustomCalendar
              selectedDate={endDate}
              onDateChange={handleEndDateChange}
            />
          </div>
        )}
      </div>

      {/* Duration Dropdown */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          Duration<span className="text-red-500">*</span>:
        </label>
        <Select
          options={durationOptions}
          value={duration}
          onChange={handleDurationChange}
          styles={customSelectStyles}
          placeholder="Select Duration"
          isClearable
          onMenuOpen={() => setShowEndCalendar(false)} // Close calendar when dropdown opens
        />
      </div>
    </div>
  );
};

export default NewSelectorCard;
